import React from 'react';
import { Container, Box, Typography, Grid } from '@mui/material';
import {
	CloudUpload as CloudUploadIcon,
	Settings as SettingsIcon,
	Visibility as VisibilityIcon,
	Download as DownloadIcon
} from '@mui/icons-material';
import { theme, commonStyles } from '../../../styles/theme';
import { USD_PRICE_PER_TOKEN } from './homePageData.jsx';

const HowItWorksTab = () => {
	const steps = [
		{ 
			icon: <CloudUploadIcon />, 
			title: 'Upload Your Layers', 
			desc: 'Organize your artwork into hierarchical layers and upload your images. GraveForge automatically generates thumbnails and processes files for optimal performance.',
			details: [
				'Drag-and-drop interface with bulk upload support',
				'Unlimited layers organized by trait categories',
				'Auto-generated thumbnails and previews',
				'Supports PNG, JPG, JPEG, and WebP formats'
			]
		},
		{ 
			icon: <SettingsIcon />, 
			title: 'Configure Rules & Paths', 
			desc: 'Set rarity distributions, create decision tree paths for variant collections, and define intelligent rules to control trait combinations.',
			details: [
				'Percentage-based or tier-based rarity',
				'Decision tree paths with weighted branches',
				'Block and force rules for trait control',
				'Real-time probability calculator'
			]
		},
		{ 
			icon: <VisibilityIcon />, 
			title: 'Preview & Test', 
			desc: 'Generate unlimited watermarked previews to validate your configuration. Test rarity distribution and analyze trait frequencies.',
			details: [
				'Instant preview generation (completely free)',
				'Path tracebacks for each NFT',
				'Batch preview generation (10-100 samples)',
				'Rarity distribution analytics'
			]
		},
		{ 
			icon: <DownloadIcon />, 
			title: 'Export & Deploy', 
			desc: `Pay only $${USD_PRICE_PER_TOKEN.toFixed(2)} per NFT and export your complete collection with high-resolution images and metadata formatted for your target blockchain.`,
			details: [
				'Pay with SOL, ETH, DEADS token, or Credit Card',
				'Metadata for Solana, Ethereum, Base, Abstract',
				'High-resolution, watermark-free images',
				'Server-side generation for 50K+ collections'
			]
		}
	];

	return (
		<Container maxWidth="lg" sx={{ py: { xs: 4, md: 8 }, px: { xs: 2, sm: 3 } }}>
			<Box sx={{ textAlign: 'center', mb: { xs: 4, md: 8 } }}>
				<Typography variant="h3" sx={{ fontWeight: 800, color: theme.colors.textPrimary, mb: 2 }}>
					How It Works
				</Typography>
				<Typography variant="body1" sx={{ color: theme.colors.textSecondary, maxWidth: 700, mx: 'auto', lineHeight: 1.7 }}>
					Create professional NFT collections in 4 straightforward steps
				</Typography>
			</Box>
			<Grid container spacing={{ xs: 2, md: 4 }}>
				{steps.map((step, idx) => (
					<Grid item xs={12} md={6} key={idx}>
						<Box sx={{ 
							...commonStyles.card, 
							p: 3,
							height: '100%',
							transition: 'all 0.3s',
							'&:hover': {
								borderColor: '#dc2626',
								transform: 'translateY(-4px)',
								boxShadow: '0 8px 24px rgba(220,38,38,0.3)'
							}
						}}>
							<Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
								<Box sx={{ p: 1.5, borderRadius: 2, bgcolor: 'rgba(220,38,38,0.2)', color: theme.colors.primary }}>
									{React.cloneElement(step.icon, { sx: { fontSize: 28 } })}
								</Box>
								<Typography variant="h6" sx={{ fontWeight: 700, color: '#fff' }}>
									{step.title}
								</Typography>
							</Box>
							<Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.8)', mb: 2, lineHeight: 1.7 }}>
								{step.desc}
							</Typography>
							<Box component="ul" sx={{ pl: 2.5, m: 0 }}>
								{step.details.map((detail, detailIdx) => (
									<Typography component="li" key={detailIdx} variant="body2" sx={{ color: 'rgba(255,255,255,0.6)', mb: 0.5, fontSize: '0.875rem' }}>
										{detail}
									</Typography>
								))}
							</Box>
						</Box>
					</Grid>
				))}
			</Grid>
		</Container>
	);
};

export default HowItWorksTab;
